import { getConfigTenant, isCorpTenant } from "@capone/common";
import { clsx } from "clsx";
import {
  GenericInfoPopup,
  Icon,
  IconName,
  paymentTextConstants,
  useDeviceTypes,
} from "halifax";
import queryStringParser from "query-string";
import React, { useContext, useState } from "react";
import { RouteComponentProps } from "react-router";
import {
  getSubtitleTrackingValue,
  MODAL_ALERT,
  MODAL_ALERT_CHOICE,
  ModalAlertProperties,
  ModalButtonType,
  ModalCategoryType,
  ModalScreens,
} from "redmond";
import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { ClientContext } from "../../../../App";
import {
  goToAvailability,
  goToShop,
} from "../../../shop/utils/queryStringHelpers";
import {
  CHOOSE_ANOTHER_HOTEL,
  CHOOSE_ANOTHER_RATE,
  CONFIRM_AND_BOOK,
  CONTINUE,
  EDIT_PAYMENT_INFO,
  ERROR_ICON,
  MODIFY_TRAVELERS,
  PRICE_DECREASE_ICON,
  PRICE_INCREASE_ICON,
  SEARCH_AGAIN,
  TRY_AGAIN,
  UNABLED_ICON,
  UPDATE_PAYMENT_INFO,
} from "../../reducer/selectors/textConstants";
import { AGENT_FEE } from "../index";
import { BookingErrorModalConnectorProps } from "./container";
import "./styles.scss";
import dayjs from "dayjs";

export interface IBookingErrorModalProps
  extends BookingErrorModalConnectorProps,
    RouteComponentProps {}

export const BookingErrorModal = ({
  hasError,
  errorTitles,
  history,
  scheduleBook,
  lodging,
  fromDate,
  untilDate,
  adultsCount,
  childrenCount,
  isBookingValid,
  acknowledgePriceDifference,
  resetBookErrors,
  modalType,
  priceQuoteSuccessful,
  ancillaries,
}: IBookingErrorModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { matchesMobile } = useDeviceTypes();
  const clientContext = useContext(ClientContext);
  const { isAgentPortal } = clientContext;

  const { primaryButtonText, secondaryButtonText } = errorTitles;

  const isCorporate = isCorpTenant(getConfigTenant());

  // modalType maps 1:1 to the getErrorTitles branch (same order as getModalType),
  // so this keeps the modal event's `step` consistent with the selector's.
  const stepByModalType: Record<string, string> = {
    pc_price_difference: "save_travelers",
    payment_verification_failed: "select_payment",
    confirmation_details_failed: "confirm_and_book",
    schedule_book_failed: "confirm_and_book",
    schedule_price_quote_failed: "save_travelers",
    poll_price_quote_failed: "confirm_and_book",
  };

  const modalEventProperties: ModalAlertProperties = {
    lob: "hotel",
    type: modalType,
    screen: ModalScreens.HOTELS_CHECKOUT,
    primary_button: primaryButtonText || "",
    secondary_button: secondaryButtonText || "",
    category: ModalCategoryType.TROUBLE,
    modal_subtitle: getSubtitleTrackingValue(errorTitles),
    modal_title: errorTitles.title || "",
    agent_title: errorTitles.agentTitle || errorTitles.title || "",
    agent_subtitle:
      errorTitles.agentSubtitle || getSubtitleTrackingValue(errorTitles),
    funnel: "premier_collection",
    step: stepByModalType[modalType] || "save_travelers",
  };

  React.useEffect(() => {
    setIsOpen(hasError);

    if (hasError)
      trackEvent({
        eventName: MODAL_ALERT,
        properties: modalEventProperties,
      });
  }, [hasError]);

  const handleButtonClick = (actionKey: string, isPrimary = false) => {
    setIsOpen(false);
    trackEvent({
      eventName: MODAL_ALERT_CHOICE,
      properties: {
        ...modalEventProperties,
        button_choice: isPrimary
          ? ModalButtonType.PRIMARY
          : ModalButtonType.SECONDARY,
      },
    });

    switch (actionKey) {
      case SEARCH_AGAIN:
        resetBookErrors();
        redirectToShop();
        break;
      case TRY_AGAIN:
        acknowledgePriceDifference();
        if (isBookingValid && priceQuoteSuccessful) {
          resetBookErrors();
          redoBook();
        } else {
          redirectToShop();
        }
        break;
      case CHOOSE_ANOTHER_RATE:
        redirectToShop();
        break;
      case CHOOSE_ANOTHER_HOTEL:
        redirectToAvailability();
        break;
      case UPDATE_PAYMENT_INFO:
      case CONTINUE:
        acknowledgePriceDifference();
        break;
      case CONFIRM_AND_BOOK:
        acknowledgePriceDifference();
        redoBook();
        break;
      case MODIFY_TRAVELERS:
        resetBookErrors();
        scrollToTravelers();
        break;
      case EDIT_PAYMENT_INFO:
        resetBookErrors();
        break;
      default:
        break;
    }
  };

  const redoBook = () => {
    const queryString = queryStringParser.parse(history.location.search);
    scheduleBook({
      agentFee: isAgentPortal ? AGENT_FEE : 0,
      isRecommended: queryString.recommended === "true",
      ancillaries: ancillaries,
      initialTime: dayjs().toISOString(),
    });
  };

  const redirectToShop = () => {
    goToShop({ history });
  };

  const redirectToAvailability = () => {
    goToAvailability({
      history,
      lodging,
      fromDate,
      untilDate,
      adultsCount,
      childrenCount,
    });
  };

  const scrollToTravelers = () => {
    const travelerSectionElement = document.querySelector(
      ".traveler-select-workflow-container"
    );

    const BANNER_HEIGHT =
      document
        .getElementById("b2bportal-banner-container")
        ?.getBoundingClientRect().height ?? 0;
    const HEADER_HEIGHT = matchesMobile
      ? 0
      : document.querySelector(".app-header")?.getBoundingClientRect().height ??
        0;
    const MARGIN = matchesMobile ? 18 : 20;

    const yOffset =
      window.pageYOffset - (BANNER_HEIGHT + HEADER_HEIGHT + MARGIN);

    const y =
      (travelerSectionElement?.getBoundingClientRect().top ?? 0) + yOffset;

    window.scrollTo({ top: y, behavior: "smooth" });
  };

  // buttons intentionally saved in reverse order for rendering

  const secondaryButton = {
    buttonText: secondaryButtonText || SEARCH_AGAIN,
    defaultStyle: "h4r-secondary",
    onClick: () => handleButtonClick(secondaryButtonText || SEARCH_AGAIN),
  };

  const primaryButton = {
    buttonText: primaryButtonText || CHOOSE_ANOTHER_HOTEL,
    defaultStyle: "h4r-primary",
    onClick: () =>
      handleButtonClick(primaryButtonText || CHOOSE_ANOTHER_HOTEL, true),
  };

  const isPaymentError =
    primaryButtonText === paymentTextConstants.ADD_NEW_PAYMENT_METHOD ||
    primaryButtonText === EDIT_PAYMENT_INFO;

  const isSingleButton = isCorpTenant(config.TENANT) && isPaymentError;

  const getIconImage = (type: string) => {
    const iconMap = {
      [ERROR_ICON]: isCorpTenant(config.TENANT) ? (
        <Icon className="error-icon" name={IconName.Caution} />
      ) : (
        <Icon className="error-icon" name={IconName.ErrorState} />
      ),
      [UNABLED_ICON]: (
        <Icon className="error-icon" name={IconName.UnableToProcess} />
      ),
      [PRICE_DECREASE_ICON]: (
        <Icon className="error-icon" name={IconName.ModalPriceDrop} />
      ),
      [PRICE_INCREASE_ICON]: (
        <Icon className="error-icon" name={IconName.PriceIncrease} />
      ),
    };

    return iconMap[type] || iconMap[ERROR_ICON];
  };

  return (
    <GenericInfoPopup
      agentSubtitle={errorTitles.agentSubtitle}
      agentTitle={errorTitles.agentTitle}
      buttons={
        isSingleButton ? [primaryButton] : [secondaryButton, primaryButton]
      }
      className={clsx("booking-error-modal", {
        mobile: matchesMobile,
        noIconPadding: isCorporate && errorTitles.icon === ERROR_ICON,
      })}
      image={getIconImage(errorTitles.icon as string)}
      isAgent={isAgentPortal}
      isMobile={matchesMobile}
      open={isOpen}
      subtitle={errorTitles.subtitle}
      title={errorTitles.title}
      // note: mobile popovers in the checkout workflow have z-index: 1300
      zIndex={matchesMobile ? 1301 : 1300}
    />
  );
};
